import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useMemo, useEffect } from 'react';
import { Flex, Box, Input, ChainIcon, Text, Button, AccessibleList, AccessibleListItem } from '../../primitives/index.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import Fuse from 'fuse.js';
import { EventNames } from '../../../constants/events.js';
import AllChainsLogo from '../../../img/AllChainsLogo.js';
import { TagPill } from './TagPill.js';
import { groupChains } from '../../../utils/tokenSelector.js';
const fuseSearchOptions = {
    includeScore: true,
    includeMatches: true,
    threshold: 0.2,
    keys: ['id', 'name', 'displayName']
};
export const ChainFilterSidebar = ({ options, value, isOpen, onSelect, onAnalyticEvent, onInputRef, tokenSearchInputRef, popularChainIds }) => {
    const [chainSearchInput, setChainSearchInput] = useState('');
    const chainFuse = new Fuse(options, fuseSearchOptions);
    const activeChainRef = useRef(null);
    const [hasScrolledOnOpen, setHasScrolledOnOpen] = useState(false);
    const { allChainsOption, popularChains, alphabeticalChains } = useMemo(() => groupChains(options, popularChainIds), [options, popularChainIds]);
    const filteredChains = useMemo(() => {
        if (chainSearchInput.trim() === '') {
            return null; // Return null to show organized sections
        }
        // Remove duplicates from search results
        const results = chainFuse.search(chainSearchInput);
        const uniqueChains = new Map();
        results.forEach((result) => {
            if (!uniqueChains.has(result.item.id)) {
                uniqueChains.set(result.item.id, result.item);
            }
        });
        return Array.from(uniqueChains.values());
    }, [chainSearchInput, chainFuse]);
    useEffect(() => {
        if (activeChainRef.current && isOpen && !hasScrolledOnOpen) {
            const timer = setTimeout(() => {
                activeChainRef.current?.scrollIntoView({
                    behavior: 'instant',
                    block: 'nearest'
                });
                setHasScrolledOnOpen(true);
            }, 100);
            return () => clearTimeout(timer);
        }
        else if (!isOpen) {
            setHasScrolledOnOpen(false);
            activeChainRef.current = null;
        }
    }, [activeChainRef.current, isOpen, hasScrolledOnOpen]);
    return (_jsx(Flex, { direction: "column", css: {
            maxWidth: 212,
            flexShrink: 0,
            gap: '1',
            bg: 'gray3',
            borderRadius: 12,
            p: '3'
        }, children: _jsxs(AccessibleList, { onSelect: (selectedValue) => {
                if (selectedValue === 'input')
                    return;
                if (selectedValue) {
                    const chain = selectedValue === 'all-chains'
                        ? { id: undefined, name: 'All Chains' }
                        : options.find((chain) => chain.id?.toString() === selectedValue);
                    if (chain) {
                        onSelect(chain);
                        onAnalyticEvent?.(EventNames.CURRENCY_STEP_CHAIN_FILTER, {
                            chain: chain.name,
                            chain_id: chain.id,
                            search_term: chainSearchInput
                        });
                    }
                }
            }, css: {
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100%'
            }, children: [_jsx(AccessibleListItem, { value: "input", asChild: true, children: _jsx(Input, { ref: onInputRef, placeholder: "Search chains", icon: _jsx(Box, { css: { color: 'gray9' }, children: _jsx(FontAwesomeIcon, { icon: faMagnifyingGlass, width: 16, height: 16 }) }), containerCss: {
                            width: '100%',
                            height: 40,
                            mb: '2'
                        }, css: {
                            width: '100%',
                            _placeholder_parent: {
                                textOverflow: 'ellipsis'
                            },
                            '--borderColor': 'colors.subtle-border-color',
                            border: '1px solid var(--borderColor)',
                            backgroundColor: 'modal-background'
                        }, value: chainSearchInput, onChange: (e) => setChainSearchInput(e.target.value) }) }), _jsx(Flex, { direction: "column", css: {
                        flex: 1,
                        overflowY: 'auto',
                        gap: '1',
                        scrollbarColor: 'var(--relay-colors-gray5) transparent'
                    }, children: filteredChains ? (
                    // Show search results without sections
                    filteredChains.map((chain) => {
                        const tag = 'tags' in chain ? chain.tags?.[0] : undefined;
                        const active = value.id === chain.id;
                        return (_jsx(ChainFilterRow, { chain: chain, isActive: active, tag: tag, onClick: (e) => {
                                if (e.detail > 0) {
                                    tokenSearchInputRef?.focus();
                                }
                            }, value: chain.id?.toString() ?? 'all-chains' }, chain.id?.toString() ?? 'all-chains'));
                    })) : (
                    // Show organized sections
                    _jsxs(_Fragment, { children: [allChainsOption && (_jsx(_Fragment, { children: _jsx(ChainFilterRow, { chain: allChainsOption, isActive: value.id === undefined, onClick: (e) => {
                                        if (e.detail > 0) {
                                            tokenSearchInputRef?.focus();
                                        }
                                    }, value: "all-chains" }, "all-chains") })), popularChains.length > 0 && (_jsxs(_Fragment, { children: [_jsx(Text, { style: "subtitle2", color: "subtle", css: { px: '2', py: '1' }, children: "Popular Chains" }), popularChains.map((chain) => {
                                        const tag = 'tags' in chain ? chain.tags?.[0] : undefined;
                                        const active = value.id === chain.id;
                                        return chain.id ? (_jsx(ChainFilterRow, { chain: chain, isActive: active, tag: tag, onClick: (e) => {
                                                if (e.detail > 0) {
                                                    tokenSearchInputRef?.focus();
                                                }
                                            }, activeChainRef: activeChainRef, value: chain.id?.toString() }, chain.id?.toString())) : null;
                                    })] })), _jsx(Text, { style: "subtitle2", color: "subtle", css: { px: '2', py: '1' }, children: "Chains A-Z" }), alphabeticalChains.map((chain) => {
                                const tag = 'tags' in chain ? chain.tags?.[0] : undefined;
                                const active = value.id === chain.id;
                                return chain.id ? (_jsx(ChainFilterRow, { chain: chain, isActive: active, tag: tag, onClick: (e) => {
                                        if (e.detail > 0) {
                                            tokenSearchInputRef?.focus();
                                        }
                                    }, activeChainRef: activeChainRef, value: chain.id?.toString() }, chain.id?.toString())) : null;
                            })] })) })] }) }));
};
const ChainFilterRow = ({ chain, isActive, onClick, tag, value, activeChainRef }) => {
    return (_jsx(AccessibleListItem, { value: value, asChild: true, children: _jsxs(Button, { color: "ghost", size: "none", onClick: onClick, ref: isActive ? activeChainRef : null, css: {
                p: '2',
                display: 'flex',
                alignItems: 'center',
                gap: '2',
                position: 'relative',
                ...(isActive && {
                    backgroundColor: 'gray6'
                }),
                transition: 'backdrop-filter 250ms linear',
                _hover: {
                    backgroundColor: isActive ? 'gray6' : 'gray/10'
                },
                '--focusColor': 'colors.focus-color',
                _focus: {
                    boxShadow: 'inset 0 0 0 2px var(--focusColor)'
                }
            }, children: [chain.id ? (_jsx(ChainIcon, { chainId: chain.id, square: true, width: 24, height: 24 })) : (_jsx(AllChainsLogo, { style: { width: 24, height: 24 } })), _jsx(Text, { style: "subtitle1", ellipsify: true, children: ('displayName' in chain && chain.displayName) || chain.name }), tag && _jsx(TagPill, { tag: tag })] }) }));
};
//# sourceMappingURL=ChainFilterSidebar.js.map